import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

const FormularioPublico = () => {
  const { link_unico } = useParams();
  const navigate = useNavigate();
  const [formulario, setFormulario] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFormulario();
  }, [link_unico]);

  const fetchFormulario = async () => {
    try {
      const res = await fetch(`${API_URL}/api/formularios-generales/${link_unico}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Formulario no encontrado o no está activo');
        } else {
          setError('Error al cargar el formulario');
        }
        setLoading(false);
        return;
      }

      const data = await res.json();
      setFormulario(data.formulario);
      setPreguntas(data.preguntas);

      // Inicializar respuestas vacías
      const initialRespuestas = {};
      data.preguntas.forEach(p => {
        initialRespuestas[p.id_pregunta] = { texto: '', archivo: null };
      });
      setRespuestas(initialRespuestas);

    } catch (err) {
      setError('Error de conexión al servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (idPregunta, value, type = 'texto') => {
    setRespuestas(prev => ({
      ...prev,
      [idPregunta]: {
        ...prev[idPregunta],
        [type]: value
      }
    }));
  };

  const handleFileChange = (e, idPregunta) => {
    const file = e.target.files[0];
    if (file) {
      handleInputChange(idPregunta, file, 'archivo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const verifyRes = await fetch(`${API_URL}/api/verify`, {
        credentials: 'include'
      });

      if (!verifyRes.ok) {
        localStorage.setItem('redirectAfterLogin', `/formulario-general/${link_unico}`);
        navigate('/login');
        return;
      }

      const respuestasParaEnviar = Object.entries(respuestas).map(([id_pregunta, data]) => ({
        id_pregunta: parseInt(id_pregunta),
        texto: data.texto,
        archivo: data.archivo ? data.archivo.name : null
      }));

      const submitRes = await fetch(`${API_URL}/api/formularios-generales/${link_unico}/responder`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          respuestas: respuestasParaEnviar
        })
      });

      if (!submitRes.ok) {
        throw new Error('Error al enviar respuestas');
      }

      // Mostrar mensaje de éxito
      alert('¡Respuestas enviadas correctamente!');
      navigate('/dashboard');

    } catch (err) {
      setError(err.message || 'Error al enviar respuestas');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box maxWidth="md" mx="auto" p={3}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {formulario?.titulo}
          </Typography>
          {formulario?.descripcion && (
            <Typography variant="body1" color="textSecondary" paragraph>
              {formulario.descripcion}
            </Typography>
          )}

          <form onSubmit={handleSubmit}>
            <List>
              {preguntas.map((pregunta) => (
                <ListItem key={pregunta.id_pregunta} divider>
                  <ListItemText
                    primary={pregunta.titulo}
                    secondary={pregunta.descripcion}
                  />

                  <Box width="100%" mt={2}>
                    {pregunta.tipo === 'documento' ? (
                      <Box>
                        <input
                          accept="*"
                          id={`file-${pregunta.id_pregunta}`}
                          type="file"
                          onChange={(e) => handleFileChange(e, pregunta.id_pregunta)}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor={`file-${pregunta.id_pregunta}`}>
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                          >
                            Subir archivo
                          </Button>
                        </label>
                        {respuestas[pregunta.id_pregunta]?.archivo && (
                          <Typography variant="body2" color="textSecondary">
                            Archivo: {respuestas[pregunta.id_pregunta].archivo.name}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Ingrese su respuesta"
                        value={respuestas[pregunta.id_pregunta]?.texto || ''}
                        onChange={(e) => handleInputChange(pregunta.id_pregunta, e.target.value)}
                      />
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>

            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                {submitting ? 'Enviando...' : 'Enviar Respuestas'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FormularioPublico;